<template>
    <div class="muven-container">
        <div class="muven-router-name-container">
            <p class="muven-router-name">Configurações do canal</p>
        </div>

        <div class="muven-content">
            <Breadcrumb :items="breadcrumbList" class="mb-3" v-if="!loading && (hasChannel || loadError)"/>

            <div class="muven-tip muven-tip-standalone tip-warning shopify-availability-alert mb-3" v-if="!loading && hasChannel && showShopifyAvailabilityAlert">
                <fa icon="exclamation-triangle" class="tip-icon" />
                <p class="mb-2"><strong>Canal Shopify - disponível com ressalvas</strong></p>

                <div class="shopify-alert-body" :class="{ 'is-collapsed': !shopifyAlertExpanded }" :style="shopifyAlertBodyStyle">
                    <div ref="shopifyAlertContent">
                        <p class="mb-2">A integração com a Shopify está disponível no Muven, porém atualmente não é o canal que recomendamos como primeira opção.</p>
                        <p class="mb-2">Isso ocorre por fatores operacionais que impactam a experiência no Brasil, como limitações no tratamento de endereços e maior complexidade no processo de configuração e autenticação, entre outras situações.</p>
                        <p class="mb-2">Dependendo do plano contratado na Shopify, a plataforma pode não disponibilizar os dados completos dos clientes que realizaram as compras. Essa limitação inviabiliza a emissão de notas fiscais, comprometendo o cumprimento das obrigações legais no Brasil.</p>
                        <p class="mb-2">Vale lembrar também que integrações como essa estão em constante evolução, e mudanças feitas pela própria Shopify podem exigir uma nova homologação do Muven. Esse processo de revisão, sem prazo definido (pode levar dias ou semanas), é conduzido por equipes que nem sempre conhecem as particularidades do mercado brasileiro - o que, não raro, pode gerar confusões na análise e bloqueios temporários fora do nosso controle.</p>
                        <p class="mb-2">Nosso time continua trabalhando para mitigar esses pontos e melhorar a experiência com a plataforma. Ainda assim, para operações que exigem maior fluidez e aderência ao cenário brasileiro, recomendamos avaliar outros canais disponíveis no Muven.</p>
                        <p class="mb-0">Caso opte pela Shopify, nossa equipe está à disposição para apoiar na configuração e garantir o melhor funcionamento possível dentro das limitações atuais.</p>
                    </div>
                </div>

                <button type="button" class="shopify-alert-toggle" @click="toggleShopifyAlert">
                    <span>{{ shopifyAlertExpanded ? 'Mostrar menos' : 'Saiba mais' }}</span>
                    <fa icon="chevron-down" class="shopify-alert-toggle-icon" :class="{ 'is-rotated': shopifyAlertExpanded }" />
                </button>
            </div>

            <Loading page v-if="loading" />

            <ResourceNotFound
                v-if="!loading && loadError"
                resource="canal"
                :identifier="$route.params.id"
                list-path="/canais"
                list-label="Voltar para canais"
                :kind="loadErrorKind"
                :error-message="loadErrorMessage"
                @retry="load" />

            <div class="channel-settings" v-if="!loading && hasChannel">
                <section class="cs-hero" :style="heroStyle">
                    <div class="cs-hero-text">
                        <BrandTileCmp class="cs-hero-tile" :size="76" :image="markUrl" :icon="channel.isCustom() ? 'store-alt' : null"
                            :text="brand.monogram" :background="brand.color" :color="brand.textColor" />

                        <div class="cs-hero-heading">
                            <h1 class="cs-hero-title">{{channel.getRealName()}}</h1>
                            <span class="cs-status" :class="channel.isActive ? 'is-active' : 'is-inactive'">
                                <span class="cs-status-dot"></span>
                                {{channel.isActive ? 'Ativo' : 'Inativo'}}
                            </span>
                        </div>

                        <p class="cs-hero-alias" v-if="channel.hasCustomizedName()">
                            <fa icon="tags" /> {{channel.name}}
                        </p>

                        <p class="cs-hero-lead">
                            Gerencie todas as configurações do seu canal {{channel.getRealName()}}.
                            Integre seus produtos, estoque, preços e muito mais.
                        </p>
                    </div>

                    <ChannelHeroArtCmp class="cs-hero-art" :channel="channel" />
                </section>

                <section class="cs-sheet">
                    <header class="cs-sheet-header">
                        <h2 class="cs-sheet-title">O que você quer configurar?</h2>
                        <p class="cs-sheet-subtitle">Selecione uma das opções abaixo para configurar e gerenciar o seu canal.</p>
                    </header>

                    <div class="cs-groups">
                        <div class="cs-group" v-for="group in optionGroups" :key="group.key" :class="{ 'is-compact': group.items.length <= 2 }">
                            <h3 class="cs-group-label">{{group.label}}</h3>

                            <div class="cs-grid">
                                <button type="button" class="cs-option" v-for="item in group.items" :key="item.title + item.index"
                                    :class="{ 'has-alert': !!item.alert }" :style="{ '--i': item.index }" @click="item.option.goto()">
                                    <span class="cs-option-top">
                                        <span class="cs-option-icon"><fa :icon="item.option.icon" /></span>
                                        <span class="cs-option-go"><fa icon="chevron-right" /></span>
                                    </span>

                                    <span class="cs-option-title">{{item.title}}</span>
                                    <span class="cs-option-description">{{item.option.getDescription()}}</span>

                                    <span class="cs-option-chip is-alert" v-if="item.alert">
                                        <fa icon="exclamation-triangle" class="cs-option-chip-icon" />
                                        {{item.alert}}
                                    </span>
                                    <span class="cs-option-chip" v-else>
                                        <fa :icon="item.option.chipIcon" class="cs-option-chip-icon" />
                                        {{item.option.getChip()}}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import { ChannelModel } from '@/channel/models/ChannelModel'
import channelService from '@/channel/services/ChannelService'
import { EntityEnum } from '@c2b/muven-commons'
import GetAuditUrl from '../../utils/mixins/GetAuditUrl'
import { mapGetters, mapState } from 'vuex'
import subscriberManager from '@/subscriber/managers/SubscriberManager'
import ChannelHeroArtCmp from '@/channel/components/ChannelHeroArtCmp.vue'
import BrandTileCmp from '@/utils/components/BrandTileCmp.vue'
import { getChannelBrand, getChannelGlow } from '@/channel/models/ChannelBrand'
import ResourceLoadError from '../../utils/mixins/ResourceLoadError'
import { getChannelNotFoundBreadcrumb } from '../utils/ChannelNotFoundBreadcrumb'

export default {
    name: 'ChannelSettingsListView',
    components: {
        ChannelHeroArtCmp,
        BrandTileCmp,
    },
    data() {
        return {
            EntityEnum,
            loading: true,
            syncingInfo: false,
            channel: new ChannelModel(),
            breadcrumbList: [],
            shopifyAlertExpanded: false,
            shopifyAlertContentHeight: null,
            optionSettingsList: [
                {
                    title: 'Configurações gerais',
                    icon: 'sliders-h',
                    group: 'connection',
                    chipIcon: 'cogs',
                    getChip: () => 'Status, preços, estoque e regras',
                    getAlert: () => this.channel.isActive ? null : 'Canal inativo - ative aqui',
                    getDescription: () => 'Ative ou inative o canal, configure a disponibilidade de estoque, variação de preço e muito mais',
                    isVisible: () => true,
                    goto: () => {
                        if (!this.hasAtLeastOneStore) {
                            this.$floatingAlert.error({
                                title: '',
                                message: `Não existe nenhuma loja vinculada ao Muven, por este motivo não é possível modificar as configurações do canal.`
                            })

                            return
                        }


                        this.$router.push(`/canais/${this.channel.id}/configuracoes/estoque-precos-e-mais`)
                    },
                },
                {
                    title: 'Autenticação',
                    icon: 'key',
                    group: 'connection',
                    chipIcon: 'link',
                    getChip: () => `Conecte sua conta ${this.channel.getRealName()}`,
                    getDescription: () => `Siga o passo a passo para integrar o Muven com o ${this.channel.getRealName()}`,
                    isVisible: () => this.channel.isMercadoLivre() ||
                        this.channel.isNuvemShop() ||
                        this.channel.isShopee() ||
                        this.channel.isMagazineLuiza() ||
                        this.channel.isShein() ||
                        this.channel.isTiktokShop() ||
                        this.channel.isShopify(),

                    goto: () => this.$router.push(`/canais/${this.channel.id}/configuracoes/autenticacao-passo-a-passo`),
                },
                {
                    title: 'Credenciais',
                    icon: 'lock',
                    group: 'connection',
                    chipIcon: 'link',
                    getChip: () => 'Chaves e tokens de acesso',
                    getDescription: () => 'Informe as credenciais de acesso do canal',
                    isVisible: () => !this.channel.isMercadoLivre() &&
                        !this.channel.isNuvemShop() &&
                        !this.channel.isShopee() &&
                        !this.channel.isMagazineLuiza() &&
                        !this.channel.isShein() &&
                        !this.channel.isTiktokShop() &&
                        !this.channel.isShopify(),
                    goto: () => this.$router.push(`/canais/${this.channel.id}/configuracoes/credenciais`),
                },
                {
                    title: 'Associar produtos',
                    icon: 'cube',
                    group: 'associations',
                    chipIcon: 'link',
                    getChip: () => 'Produtos vinculados',
                    getDescription: () => 'Vincule cada produto do canal a um produto da retaguarda',
                    isVisible: () => !(this.channel.isMercadoLivre() && this.channel.allowsMultiAssociation()) && !this.channel.hasApiLimitReq(),
                    goto: () => this.$router.push(`/canais/${this.channel.id}/configuracoes/associacoes/produtos`),
                },
                {
                    title: 'Associar produtos',
                    icon: 'cube',
                    group: 'associations',
                    chipIcon: 'link',
                    getChip: () => 'Produtos vinculados',
                    getDescription: () => 'Vincule cada produto do canal a um produto da retaguarda',
                    isVisible: () => this.channel.hasApiLimitReq(),
                    goto: () => this.$router.push(`/canais/${this.channel.id}/configuracoes/associacoes/limit_req/produtos`),
                },
                {
                    title: 'Associar produtos',
                    icon: 'cube',
                    group: 'associations',
                    chipIcon: 'link',
                    getChip: () => 'Produtos vinculados',
                    getDescription: () => 'Vincule diferentes produtos do canal a um mesmo produto da retaguarda',
                    isVisible: () => this.channel.isMercadoLivre() && this.channel.allowsMultiAssociation(),
                    goto: () => this.$router.push(`/canais/${this.channel.id}/configuracoes/associacoes/multi/produtos`),
                },
                {
                    title: 'Associar categorias',
                    icon: 'sitemap',
                    group: 'associations',
                    chipIcon: 'folder-open',
                    getChip: () => 'Mapeamento de categorias',
                    getDescription: () => 'Vincule as categorias do canal com as categorias da retaguarda',
                    isVisible: () => true,
                    goto: () => this.$router.push(`/canais/${this.channel.id}/configuracoes/associacoes/categorias`),
                },
                {
                    title: 'Associar marcas',
                    icon: 'copyright',
                    group: 'associations',
                    chipIcon: 'tags',
                    getChip: () => 'Mapeamento de marcas',
                    getDescription: () => 'Vincule as marcas do canal com as marcas da retaguarda',
                    isVisible: () => !this.channel.isNuvemShop() &&
                        !this.channel.isMercadoLivre() &&
                        !this.channel.isTiktokShop(),
                    goto: () => this.$router.push(`/canais/${this.channel.id}/configuracoes/associacoes/marcas`),
                },
                {
                    title: 'Associar formas de pagamentos',
                    icon: 'credit-card',
                    group: 'associations',
                    chipIcon: 'money-bill-wave',
                    getChip: () => 'Formas de pagamento',
                    getDescription: () => 'Vincule as formas de pagamentos do canal com as formas de pagamento da retaguarda',
                    isVisible: () => true,
                    goto: () => this.$router.push(`/canais/${this.channel.id}/configuracoes/associacoes/formas_pagamento`),
                },
                {
                    title: 'Associar transportadoras',
                    icon: 'shipping-fast',
                    group: 'associations',
                    chipIcon: 'truck-moving',
                    getChip: () => 'Transportadoras e prazos',
                    getDescription: () => 'Vincule as transportadoras do canal com as transportadoras da retaguarda',
                    isVisible: () => true,
                    goto: () => this.$router.push(`/canais/${this.channel.id}/configuracoes/associacoes/transportadoras`),
                },
                {
                    title: 'Fila de processamento',
                    icon: 'stream',
                    group: 'monitoring',
                    chipIcon: 'clock',
                    getChip: () => 'Status e histórico',
                    getDescription: () => 'Acompanhe quais informações estão aguardando processamento e quais já foram atualizadas',
                    isVisible: () => true,
                    goto: () => this.$router.push(`/canais/${this.channel.id}/configuracoes/informacoes/fila-processamento`),
                },
                {
                    title: 'Auditoria',
                    icon: 'history',
                    group: 'monitoring',
                    chipIcon: 'file-alt',
                    getChip: () => 'Histórico de alterações',
                    getDescription: () => 'Acompanhe as modificações específicas feitas neste canal',
                    isVisible: () => true,
                    goto: () => this.$router.push(this.getAuditUrl(this.channel.id, this.EntityEnum.Channel)),
                },
            ]
        }
    },
    mixins: [
        GetAuditUrl,
        ResourceLoadError,
    ],
    mounted() {
        this.load()
    },
    methods: {
        async load() {
            this.loading = true
            this.loadError = null

            try {
                this.channel = await channelService.getChannelById(this.$route.params.id) 
                this.breadcrumbList = [
                    {
                        id: 1,
                        description: `Canais`,
                        path: '/canais',
                    },
                    {
                        id: 1,
                        description: `Configurações ${this.channel.name}`,
                        channel: this.channel,
                        path: '',
                    }
                ]
            } catch (e) {
                this.loadError = e
                this.breadcrumbList = getChannelNotFoundBreadcrumb(this.$route.params.id)
            } finally {
                this.loading = false
            }
        },
        toggleShopifyAlert() {
            if (!this.shopifyAlertExpanded && this.$refs.shopifyAlertContent) {
                this.shopifyAlertContentHeight = this.$refs.shopifyAlertContent.scrollHeight
            }

            this.shopifyAlertExpanded = !this.shopifyAlertExpanded
        },
    },
    computed: {
        ...mapState({
            subscriber: state => state.subscriber,
        }),
        ...mapGetters([
            'hasAtLeastOneStore',
        ]),
        hasChannel() {
            return !!this.channel.id
        },
        optionGroups() {
            // Conexão e Acompanhamento (2 cartões cada) dividem a primeira linha;
            // Associações, o trabalho do dia a dia, ocupa a largura toda embaixo.
            const groups = [
                { key: 'connection', label: 'Conexão', items: [] },
                { key: 'monitoring', label: 'Acompanhamento', items: [] },
                { key: 'associations', label: 'Associações', items: [] },
            ]

            this.optionSettingsList
                .filter(option => option.isVisible())
                .forEach(option => {
                    groups.find(g => g.key === option.group).items.push({
                        option,
                        title: option.title,
                        alert: option.getAlert ? option.getAlert() : null,
                    })
                })

            // Índice corrido na ordem da tela: a cascata de entrada não reinicia a cada grupo
            let index = 0
            groups.forEach(g => g.items.forEach(item => { item.index = index++ }))

            return groups.filter(g => g.items.length)
        },
        brand() {
            return getChannelBrand(this.channel.enumChannel)
        },
        markUrl() {
            return this.brand.mark ? require(`@/channel/images/marks/${this.brand.mark}`) : null
        },
        heroStyle() {
            return {
                '--hero-glow': getChannelGlow(this.brand) || 'var(--brand)',
            }
        },
        showShopifyAvailabilityAlert() {
            return this.channel.isShopify() && subscriberManager.getPublicKeyFromStorage() !== 'rafael-c2b-hml'
        },
        shopifyAlertBodyStyle() {
            if (this.shopifyAlertExpanded) {
                return { maxHeight: this.shopifyAlertContentHeight ? `${this.shopifyAlertContentHeight}px` : 'none' }
            }

            return { maxHeight: '4.6em' }
        },
    },
}
</script>

<style lang="scss" scoped>
.shopify-availability-alert {
    max-width: 700px;
    margin-right: auto;
    margin-left: auto;
}

.shopify-alert-body {
    position: relative;
    overflow: hidden;
    transition: max-height .35s ease;
}

.shopify-alert-body.is-collapsed {
    mask-image: linear-gradient(to bottom, #000 calc(100% - 1.6em), transparent 100%); /* theme-ignore: canal alfa da mascara */
    -webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - 1.6em), transparent 100%); /* theme-ignore: canal alfa da mascara */
}

.shopify-alert-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    padding: 0;
    border: none;
    background: none;
    color: var(--warning-fg);
    font-size: .85em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .5px;
    cursor: pointer;
}

.shopify-alert-toggle:hover {
    color: var(--warning-fg);
    text-decoration: underline;
}

.shopify-alert-toggle-icon {
    font-size: .75em;
    transition: transform .25s ease;
}

.shopify-alert-toggle-icon.is-rotated {
    transform: rotate(180deg);
}

$ease-out: cubic-bezier(.22, 1, .36, 1);
$display: 'Bricolage Grotesque', 'Source Sans Pro', system-ui, sans-serif;

/*
 * Cor aqui é sinal, não decoração: a tela é neutra, a marca do canal aparece
 * uma vez (no topo), o roxo do Muven só na interação e amarelo/verde só quando
 * há algo a dizer (status, canal inativo).
 */

.channel-settings {
    max-width: 1240px;
    margin: 0 auto;
}

/* ── Topo: identidade do canal, sem cara de vitrine ── */

.cs-hero {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 400px);
    align-items: center;
    gap: 24px;
    min-height: 210px;
    padding: 26px 32px 58px;
    overflow: hidden;
    border: 1px solid var(--border-subtle);
    border-radius: 20px;
    background:
        radial-gradient(ellipse 50% 90% at 86% 35%, color-mix(in srgb, var(--hero-glow) 12%, transparent), transparent 70%),
        var(--surface-1);

    // Malha de pontos que some em direção ao texto
    &::before {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-image: radial-gradient(color-mix(in srgb, var(--text-strong) 9%, transparent) 1px, transparent 1.5px);
        background-size: 18px 18px;
        mask-image: linear-gradient(100deg, transparent 40%, var(--white) 80%); /* theme-ignore: canal alfa da mascara */
        -webkit-mask-image: linear-gradient(100deg, transparent 40%, var(--white) 80%); /* theme-ignore: canal alfa da mascara */
        pointer-events: none;
    }
}

.cs-hero-text {
    position: relative;
    z-index: 1;
    animation: cs-rise .6s $ease-out both;
}

.cs-hero-tile {
    margin-bottom: 14px;
    box-shadow: var(--shadow-sm), 0 10px 24px -10px color-mix(in srgb, var(--hero-glow) 40%, transparent);
}

.cs-hero-heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
}

.cs-hero-title {
    margin: 0;
    color: var(--text-strong);
    font-family: $display;
    font-size: 36px;
    font-weight: 800;
    letter-spacing: -.9px;
    line-height: 1.05;
}

.cs-status {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 3px 11px 3px 9px;
    border: 1px solid;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 700;

    &.is-active {
        color: var(--success-fg);
        background: var(--success-bg);
        border-color: var(--success-border);
    }

    &.is-inactive {
        color: var(--warning-fg);
        background: var(--warning-bg);
        border-color: var(--warning-border);
    }
}

.cs-status-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
}

.cs-hero-alias {
    margin: 8px 0 0;
    color: var(--text-muted);
    font-size: 14px;
    font-weight: 600;
}

.cs-hero-lead {
    max-width: 460px;
    margin: 10px 0 0;
    color: var(--text-muted);
    font-size: 15px;
    line-height: 1.5;
}

.cs-hero-art {
    align-self: stretch;
}

/* ── Folha das opções: sobe por cima do topo ── */

.cs-sheet {
    position: relative;
    z-index: 2;
    margin: -36px 12px 0;
    padding: 26px 28px 28px;
    background: var(--surface-1);
    border: 1px solid var(--border-subtle);
    border-radius: 18px;
    box-shadow: var(--shadow-md);
    animation: cs-rise .6s .08s $ease-out both;
}

.cs-sheet-header {
    margin-bottom: 6px;
}

.cs-sheet-title {
    margin: 0;
    color: var(--text-strong);
    font-family: $display;
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -.4px;
}

.cs-sheet-subtitle {
    margin: 4px 0 0;
    color: var(--text-muted);
    font-size: 15px;
}

/* ── Grupos ── */

.cs-groups {
    display: flex;
    flex-wrap: wrap;
    gap: 0 28px;
}

.cs-group {
    flex: 1 1 100%;
    min-width: 0;
    margin-top: 24px;

    &.is-compact {
        flex-basis: 480px;
    }
}

.cs-group-label {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0 0 12px;
    color: var(--text-faint);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .12em;
    text-transform: uppercase;

    // Fio que leva o olho até a borda: separa sem pesar
    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: var(--border-subtle);
    }
}

/* ── Cartões ── */

.cs-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 14px;
}

.cs-option {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 18px;
    border: 1px solid var(--border-subtle);
    border-radius: 16px;
    background: var(--surface-1);
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: transform .2s $ease-out, box-shadow .2s $ease-out, border-color .2s;
    animation: cs-rise .5s $ease-out both;
    animation-delay: calc(.14s + var(--i) * .035s);

    &.has-alert {
        border-color: var(--warning-border);
    }

    &:hover,
    &:focus-visible {
        border-color: var(--tint-brand-border);
        box-shadow: var(--shadow-md);
        transform: translateY(-2px);

        .cs-option-icon {
            background: var(--tint-brand-bg);
            border-color: var(--tint-brand-border);
            color: var(--brand);
        }

        .cs-option-go {
            background: var(--brand-fill);
            border-color: var(--brand-fill);
            color: var(--text-on-brand);
            transform: translateX(2px);
        }
    }

    &:focus {
        outline: none;
    }

    &:focus-visible {
        box-shadow: 0 0 0 3px var(--focus-ring), var(--shadow-md);
    }
}

.cs-option-top {
    display: flex;
    align-self: stretch;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 14px;
}

.cs-option-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border: 1px solid var(--border-subtle);
    border-radius: 13px;
    background: var(--surface-2);
    color: var(--text-soft);
    font-size: 19px;
    transition: background-color .2s, border-color .2s, color .2s;
}

.cs-option-go {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid var(--border-subtle);
    border-radius: 50%;
    background: var(--surface-1);
    color: var(--text-faint);
    font-size: 11px;
    transition: background-color .2s, border-color .2s, color .2s, transform .2s $ease-out;
}

.cs-option-title {
    color: var(--text-strong);
    font-size: 17px;
    font-weight: 700;
    line-height: 1.25;
}

.cs-option-description {
    flex: 1 1 auto;
    margin: 6px 0 14px;
    color: var(--text-muted);
    font-size: 14px;
    line-height: 1.45;
}

.cs-option-chip {
    display: flex;
    align-self: stretch;
    align-items: center;
    gap: 8px;
    padding: 6px 11px;
    border: 1px solid transparent;
    border-radius: 9px;
    background: var(--surface-2);
    color: var(--text-muted);
    font-size: 13px;
    font-weight: 600;

    &.is-alert {
        border-color: var(--warning-border);
        background: var(--warning-bg);
        color: var(--warning-fg);
    }
}

.cs-option-chip-icon {
    flex-shrink: 0;
    opacity: .75;

    .is-alert & {
        opacity: 1;
    }
}

// `translate` e nao `transform`: o fill da animacao prenderia o transform e
// mataria o "sobe" do hover dos cartoes.
@keyframes cs-rise {
    from { opacity: 0; translate: 0 12px; }
    to   { opacity: 1; translate: 0 0; }
}

/* ── Responsivo ── */

@media (max-width: 1100px) {
    .cs-hero {
        grid-template-columns: minmax(0, 1fr) minmax(0, 320px);
    }
}

@media (max-width: 900px) {
    .cs-hero {
        grid-template-columns: 1fr;
        min-height: 0;
        padding: 22px 20px 56px;
    }

    .cs-hero-art {
        display: none;
    }

    .cs-hero-title {
        font-size: 30px;
    }
}

@media (max-width: 576px) {
    .cs-sheet {
        margin: -36px 0 0;
        padding: 20px 16px;
    }

    .cs-hero-title {
        font-size: 28px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .cs-hero-text,
    .cs-sheet,
    .cs-option {
        animation: none;
    }

    .cs-option,
    .cs-option-icon,
    .cs-option-go {
        transition: none;
    }
}
</style>
